<template>
  <div :class="['day-selector cursor-pointer', containerClass]">
    <pt-form-input
      v-if="searchable"
      v-model="searchText"
      :placeholder="`${selected.length ? selected.length + ' items' : 'No item' } selected`"
      type="text"
      icon-class="fa-search"
      icon-position="left"
      @focus="showOptionList"
      @input="searchTextChange"
    />
    <div
      v-else
      class="p-2 flex justify-between input-label"
      tabindex="0"
      @focus="showOptionList"
    >
      <div class="truncate">
        <span
          v-if="!filteredName"
          class="placeholder-color"
        >
          {{ placeholder }}
        </span>
        {{ filteredName }}
      </div>

      <i class="fa fa-caret-down px-2 pt-1 text-grey-dark text-base" aria-hidden="true" />
    </div>
    <pt-list
      v-if="(filteredSelectedList.length || filteredAvailableList.length) && showList"
      class="pt-allselect-options-list"
    >
      <pt-list-item
        v-if="!searchText"
        key="select-all"
        @list-item-clicked="onSelectAllClicked"
      >
        <label class="pt-checkbox pt-checkbox-fill">
          <input
            v-if="allSelected"
            type="checkbox"
            checked
          />
          <span class="checkmark" />
        </label>
        <div class="pt-allselect__option-label">Full Week</div>
      </pt-list-item>
      <pt-list-item
        v-for="selectedOption in filteredSelectedList"
        :key="`selected-${selectedOption[trackBy]}`"
        @list-item-clicked="onOptionClicked($event, selectedOption)"
      >
        <label class="pt-checkbox pt-checkbox-fill">
          <input type="checkbox" checked />
          <span class="checkmark" />
        </label>
        <div class="pt-allselect__option-label">{{ selectedOption[labelKey] }}</div>
      </pt-list-item>

      <pt-list-item
        v-for="availableOption in filteredAvailableList"
        :key="`available-${availableOption[trackBy]}`"
        @list-item-clicked="onOptionClicked($event, availableOption, true)"
      >
        <label class="pt-checkbox">
          <span class="checkmark" />
        </label>
        <div class="pt-allselect__option-label">{{ availableOption[labelKey] }}</div>
      </pt-list-item>
    </pt-list>
    <pt-list
      v-else-if="!filteredSelectedList.length && !filteredAvailableList.length && showList"
    >
      <pt-list-item>{{ noResult }}</pt-list-item>
    </pt-list>
  </div>
</template>

<script>
import { PtFormInput } from '@pathao/ui-components/esm/FormComponents';
import { PtList, PtListItem } from '@pathao/ui-components/esm/List';
import OutsideClick from '@src/mixins/OutsideClick';

const optionMatchesSearchTxt = (lookupStr, keyword) => {
  let correctedLookupStr = lookupStr.replace(/[^\w\s]/gi, '');
  correctedLookupStr = correctedLookupStr.toLowerCase();

  let correctedKeyword = keyword.replace(/[^\w\s]/gi, '');
  correctedKeyword = correctedKeyword.toLowerCase();

  return correctedLookupStr.indexOf(correctedKeyword) > -1;
};

export default {
  name: 'DaySelector',
  components: { PtFormInput, PtList, PtListItem },
  mixins: [OutsideClick],
  props: {
    labelKey: {
      type: String,
      default: 'label',
    },
    trackBy: {
      type: [String, Number],
      default: 'id',
    },
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    noResult: {
      type: String,
      default: 'No Item Found',
    },
    placeholder: {
      type: String,
      default: 'Select option',
    },
  },
  data() {
    return {
      searchText: null,
      selected: this.value,
      available: [],
      showList: false,
    };
  },
  computed: {
    filteredName() {
      let names = this.selected.map(item => item.name);
      return names.length === 7 ? `Saturday to Friday` : names.join(', ');
    },
    allSelected() {
      return this.options.length === this.selected.length;
    },
    filteredSelectedList() {
      if (!this.searchText) {
        return this.selected;
      }

      const selectedList = this.selected.filter((item) => {
        return optionMatchesSearchTxt(item[this.labelKey], this.searchText);
      });

      return selectedList;
    },
    filteredAvailableList() {
      if (!this.searchText) {
        return this.available;
      }

      const availableList = this.available.filter((item) => {
        return optionMatchesSearchTxt(item[this.labelKey], this.searchText);
      });

      return availableList;
    },
    containerClass() {
      const classes = {
        'pt-allselect-container': true,
        'pt-allselect-container-collapsed': !this.showList,
        'pt-allselect-container-expanded': this.showList,
      };

      return classes;
    },
  },
  watch: {
    value(newValue) {
      this.selected = newValue;
      this.filterAvailableOptions();
    },
    options() {
      this.filterAvailableOptions();
    },
  },
  mounted() {
    this.filterAvailableOptions();
  },
  methods: {
    filterAvailableOptions() {
      const availableOptions = [];
      const selectedOptions = this.selected || [];

      for (let i = 0; i < this.options.length; i++) {
        const isSelected = selectedOptions.some(selectedOption => selectedOption[this.trackBy] === this.options[i][this.trackBy]);

        if (!isSelected) {
          availableOptions.push(this.options[i]);
        }
      }

      this.available = availableOptions;
    },
    onOptionClicked(event, option, isAvailable = false) {
      // Stopping propagating event higher up so that remove event doesn't get called twice
      // console.log(event);
      event.preventDefault();

      let availableList = this.available;
      let selectedList = this.selected;

      if (isAvailable) {
        availableList = availableList.filter(availableOption => {
          return availableOption[this.trackBy] !== option[this.trackBy];
        });

        selectedList.push(option);
      } else {
        selectedList = selectedList.filter(selectedOption => {
          return selectedOption[this.trackBy] !== option[this.trackBy];
        });

        availableList.push(option);
      }

      this.available = availableList;
      this.setSelected(selectedList);

      const eventType = isAvailable ? 'select' : 'remove';
      this.$emit(eventType, option);
    },
    onSelectAllClicked(event) {
      // Stopping propagating event higher up so that remove event doesn't get called twice
      event.preventDefault();

      if (this.allSelected) {
        this.available = this.options;
        this.setSelected();
      } else {
        this.available = [];
        this.setSelected(this.options);
      }

      this.$emit('select-all', this.allSelected);
    },
    onOutsideClick() {
      if (this.showList) {
        this.hideOptionList();
      }
    },
    setSelected(list = []) {
      this.selected = list;
      this.$emit('input', this.selected);
    },
    showOptionList(val, event) {
      if (!this.showList) {
        this.showList = true;
        this.$emit('open');
      }
    },
    hideOptionList() {
      this.showList = false;
      this.$emit('close');
    },
    searchTextChange(val) {
      this.$emit('search-change', val);
    },
  },
};
</script>

<style lang="scss">
  @import './DaySelector.scss';
</style>
